import type { Track } from '@pujolisique/shared';

export function fmtTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) sec = 0;
  const s = Math.floor(sec % 60);
  const m = Math.floor(sec / 60) % 60;
  const h = Math.floor(sec / 3600);
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

export function fmtLong(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  if (h) return `${h} h ${String(m).padStart(2, '0')}`;
  return `${m} min`;
}

export function plural(n: number, one: string, many: string) {
  return `${n} ${n > 1 ? many : one}`;
}

export function artistLine(t: Pick<Track, 'artists' | 'albumArtist'>) {
  return t.artists.map((a) => a.name).join(', ') || t.albumArtist || 'Artiste inconnu';
}

/** Libellé qualité : « FLAC 24 bits », « MP3 320 », etc. */
export function qualityLabel(t: Track): string | undefined {
  if (!t.format) return undefined;
  const f = t.format.toUpperCase();
  if (['FLAC', 'ALAC', 'WAV', 'AIFF', 'WV', 'APE', 'DSF'].includes(f)) {
    const bits = t.bitDepth && t.bitDepth > 16 ? ` ${t.bitDepth} bits` : '';
    const rate = t.sampleRate && t.sampleRate > 48000 ? ` ${(t.sampleRate / 1000).toFixed(1).replace('.0', '')} kHz` : '';
    return `${f}${bits}${rate}`;
  }
  return t.bitrate ? `${f} ${t.bitrate}` : f;
}

export function isHiRes(t: Track) {
  return (t.bitDepth ?? 0) > 16 || (t.sampleRate ?? 0) > 48000;
}

export function greeting(name?: string) {
  const h = new Date().getHours();
  const g = h < 5 ? 'Bonne nuit' : h < 12 ? 'Bonjour' : h < 18 ? 'Bon après-midi' : 'Bonsoir';
  const first = name?.split(/\s+/)[0];
  return first ? `${g}, ${first}` : g;
}

/** Couleur stable dérivée d'un texte (placeholders) */
export function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

export function initials(name: string) {
  const parts = name.replace(/[^\p{L}\p{N} ]/gu, ' ').split(/\s+/).filter(Boolean);
  if (!parts.length) return '♪';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} o`;
  const u = ['Ko', 'Mo', 'Go', 'To'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toLocaleString('fr-FR', { maximumFractionDigits: v < 10 ? 1 : 0 })} ${u[i]}`;
}
